import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as authApi from '@/api/auth'
import { tokenStorage } from '@/api/tokenStorage'
import type { PrincipalVO, SignInVO } from '@/types/api'

/**
 * The signed-in operator and the two tokens.
 *
 * `restore()` is what makes a page reload work: the access token lives only in memory, so on boot
 * the app re-mints one from the stored refresh token and re-reads the operator. If that fails the
 * session is simply gone and the router sends the user to the login screen.
 */
export const useAuthStore = defineStore('auth', () => {
  const operator = ref<PrincipalVO | null>(null)
  const restoring = ref(false)
  const restored = ref(false)

  const isSignedIn = computed(() => operator.value !== null)
  const permissions = computed(() => operator.value?.permissions ?? [])

  function hasPermission(permission: string): boolean {
    return permissions.value.includes(permission)
  }

  function accept(signIn: SignInVO): void {
    tokenStorage.setAccessToken(signIn.accessToken)
    tokenStorage.setRefreshToken(signIn.refreshToken)
    operator.value = signIn.operator
    restored.value = true
  }

  async function signInWithGoogle(idToken: string): Promise<void> {
    accept(await authApi.signInWithGoogle(idToken))
  }

  async function signInWithPassword(
    username: string,
    password: string,
    rememberMe: boolean
  ): Promise<void> {
    accept(await authApi.signInWithPassword(username, password, rememberMe))
  }

  /** Re-establishes the session from the stored refresh token. Never throws. */
  async function restore(): Promise<void> {
    if (restored.value || restoring.value) {
      return
    }
    restoring.value = true
    try {
      if (!tokenStorage.getRefreshToken()) {
        return
      }
      // The http layer refreshes on a 401 and retries, so asking for the operator is enough to both
      // mint an access token and load the profile.
      operator.value = await authApi.fetchCurrentOperator()
    } catch {
      tokenStorage.clear()
      operator.value = null
    } finally {
      restoring.value = false
      restored.value = true
    }
  }

  async function signOut(): Promise<void> {
    try {
      await authApi.signOut()
    } catch {
      // A failed revoke must not keep the user signed in locally.
    } finally {
      tokenStorage.clear()
      operator.value = null
    }
  }

  /** Called when the http layer finds the session unrecoverable. */
  function forget(): void {
    tokenStorage.clear()
    operator.value = null
  }

  return {
    operator,
    restoring,
    restored,
    isSignedIn,
    permissions,
    hasPermission,
    signInWithGoogle,
    signInWithPassword,
    restore,
    signOut,
    forget
  }
})
